import { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Chip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../services/api";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const DEFAULT_IMAGE =
  BASE_URL + "/static/profile_pic/default_profile_pic.png";

// ---- shared palette (matches the rest of the app) -----------------------
const palette = {
  indigo: "#4B49AC",
  indigoDark: "#37357D",
  indigoSoft: "#F4F3FB",
  ink: "#1F2937",
  slate: "#6B7280",
  line: "#E6E9ED",
  bg: "#FAFBFC",
  teal: "#12A594",
  tealSoft: "#DDF6F1",
  coral: "#FF6B57",
  coralSoft: "#FFEFEC",
};

// ---- status -> color mapping ---------------------------------------------
const STATUS_COLORS = {
  PRESENT: { bg: palette.teal, border: palette.teal },
  ABSENT: { bg: palette.coral, border: palette.coral },
  CANCELLED: { bg: "#94A3B8", border: "#94A3B8" },
  SCHEDULED: { bg: palette.slate, border: palette.slate },
};
const DEFAULT_STATUS_COLOR = { bg: palette.slate, border: palette.slate };

export default function TherapyWeekCalendar({ branchId }) {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);

  const getWeekDates = (date) => {
    const current = new Date(date);
    const day = current.getDay();
    const diff = day === 0 ? -6 : 1 - day;

    const start = new Date(current);
    start.setDate(current.getDate() + diff);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);

    return {
      start: start.toISOString().split("T")[0],
      end: end.toISOString().split("T")[0],
    };
  };

  const fetchSessions = async (date = new Date()) => {
    try {
      const { start, end } = getWeekDates(date);

      const response = await api.get(
        "app/patient/therapy/sessions/week/",
        {
          params: {
            branch_id: branchId,
            start_date: start,
            end_date: end,
          },
        },
      );

      setSessions(response?.data || []);
    } catch (error) {
      console.error("Error fetching therapy sessions:", error);
      setSessions([]);
    }
  };

  useEffect(() => {
    if (branchId) fetchSessions();
  }, [branchId]);

  const events = sessions.map((session) => {
    const color = STATUS_COLORS[session.status] || DEFAULT_STATUS_COLOR;
    return {
      id: String(session.session_id),
      title: `${session.patient_name} - ${session.service_name}`,
      start: `${session.session_date}T${session.start_time}`,
      end: `${session.session_date}T${session.end_time}`,
      backgroundColor: color.bg,
      borderColor: color.border,
      extendedProps: session,
    };
  });

  // ---- compact event content, avoids overlap on short slots ----
  const renderEventContent = (eventInfo) => {
    const session = eventInfo.event.extendedProps;
    const isShort = eventInfo.timeText === "" || eventInfo.event.end - eventInfo.event.start <= 30 * 60 * 1000;

    const image = session.profile_pic_path
      ? `${BASE_URL}${session.profile_pic_path}`
      : DEFAULT_IMAGE;

    return (
      <Box
        sx={{
          px: 0.75,
          py: 0.5,
          overflow: "hidden",
          cursor: "pointer",
          height: "100%",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            minWidth: 0,
            width: "100%",
          }}
        >
          <img
            src={image}
            alt={session.patient_name}
            style={{
              width: isShort ? 18 : 24,
              height: isShort ? 18 : 24,
              borderRadius: "50%",
              objectFit: "cover",
              flexShrink: 0,
              border: "1.5px solid rgba(255,255,255,0.6)",
            }}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = DEFAULT_IMAGE;
            }}
          />

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "#fff",
                lineHeight: 1.3,
              }}
            >
              {session.patient_name}
            </Box>

            {!isShort && (
              <>
                <Box
                  sx={{
                    fontSize: "11px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "rgba(255,255,255,0.95)",
                    lineHeight: 1.3,
                  }}
                >
                  {session.service_name}
                </Box>

                <Box
                  sx={{
                    fontSize: "10px",
                    color: "rgba(255,255,255,0.85)",
                    lineHeight: 1.3,
                  }}
                >
                  {session.status}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: "1400px",
          margin: "0 auto",
          background: "#fff",
          borderRadius: "14px",
          border: `1px solid ${palette.line}`,
          p: 2,

          // ---- FullCalendar toolbar overrides ----
          "& .fc-toolbar-title": {
            fontSize: "20px",
            fontWeight: 700,
            color: palette.ink,
          },
          "& .fc-button": {
            backgroundColor: "#fff",
            borderColor: palette.line,
            color: palette.ink,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "13px",
            boxShadow: "none",
            borderRadius: "8px !important",
            padding: "6px 14px",
          },
          "& .fc-button:hover": {
            backgroundColor: palette.indigoSoft,
            borderColor: palette.line,
            color: palette.ink,
          },
          "& .fc-button:focus": {
            boxShadow: "none",
          },
          // spacing between day/week buttons
          "& .fc-timeGridDay-button, & .fc-timeGridWeek-button": {
            marginLeft: "8px !important",
          },
          "& .fc-button-primary:not(:disabled).fc-button-active": {
            backgroundColor: palette.indigo,
            borderColor: palette.indigo,
            color: "#fff",
          },
          "& .fc-today-button": {
            backgroundColor: palette.indigo,
            borderColor: palette.indigo,
            color: "#fff",
            opacity: "1 !important",
          },
          "& .fc-today-button:hover": {
            backgroundColor: palette.indigoDark,
            borderColor: palette.indigoDark,
          },
          "& .fc-today-button:disabled": {
            backgroundColor: palette.slate,
            borderColor: palette.slate,
            opacity: "1 !important",
          },

          // ---- Grid lines / headers ----
          "& .fc-col-header-cell": {
            backgroundColor: palette.bg,
            borderColor: palette.line,
          },
          "& .fc-col-header-cell-cushion": {
            color: palette.ink,
            fontWeight: 700,
            fontSize: "13px",
            padding: "8px 4px",
          },
          "& .fc-timegrid-slot-label-cushion": {
            color: palette.slate,
            fontSize: "12px",
          },
          "& .fc-timegrid-axis, & .fc-timegrid-slot, & .fc-col-header, & .fc-scrollgrid": {
            borderColor: palette.line,
          },

          // ---- Today highlight (lighter indigo tint) ----
          "& .fc-timegrid-col.fc-day-today": {
            backgroundColor: `${palette.indigoSoft} !important`,
          },
          "& .fc-col-header-cell.fc-day-today": {
            backgroundColor: `${palette.indigoSoft} !important`,
          },
          "& .fc-col-header-cell.fc-day-today .fc-col-header-cell-cushion": {
            color: `${palette.indigo} !important`,
          },
          "& .fc-timegrid-now-indicator-line": {
            borderColor: `${palette.coral} !important`,
          },
          "& .fc-timegrid-now-indicator-arrow": {
            borderColor: `${palette.coral} !important`,
            color: palette.coral,
          },

          // ---- Events ----
          "& .fc-timegrid-event": {
            borderRadius: "6px",
            border: "none",
          },
          "& .fc-event-main": {
            padding: 0,
            height: "100%",
          },
          "& .fc-timegrid-event-harness": {
            minHeight: "28px",
          },
        }}
      >
        <FullCalendar
          plugins={[timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          firstDay={1}
          height="auto"
          contentHeight="auto"
          slotMinTime="08:00:00"
          slotMaxTime="20:00:00"
          slotDuration="00:30:00"
          allDaySlot={false}
          nowIndicator
          weekends
          events={events}
          eventContent={renderEventContent}
          eventClick={(info) => {
            setSelectedSession(info.event.extendedProps);
          }}
          datesSet={(info) => {
            fetchSessions(info.start);
          }}
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "timeGridDay,timeGridWeek",
          }}
          views={{
            timeGridDay: { buttonText: "day" },
            timeGridWeek: { buttonText: "week" },
          }}
        />
      </Box>

      <Dialog
        open={Boolean(selectedSession)}
        onClose={() => setSelectedSession(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: palette.ink }}>
          Session details
          <IconButton
            onClick={() => setSelectedSession(null)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: palette.slate,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {selectedSession && (
            <Box sx={{ py: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <img
                  src={
                    selectedSession.profile_pic_path
                      ? `${BASE_URL}${selectedSession.profile_pic_path}`
                      : DEFAULT_IMAGE
                  }
                  alt={selectedSession.patient_name}
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: `2px solid ${palette.line}`,
                  }}
                />

                <Box>
                  <Box sx={{ fontSize: 18, fontWeight: 700, color: palette.ink }}>
                    {selectedSession.patient_name}
                  </Box>

                  <Box sx={{ color: palette.slate, fontSize: 14 }}>
                    {selectedSession.service_name}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1.2 }}>
                <Box sx={{ display: "flex", gap: 1, fontSize: 14 }}>
                  <Box sx={{ color: palette.slate, minWidth: 90 }}>Session</Box>
                  <Box sx={{ color: palette.ink, fontWeight: 600 }}>
                    {selectedSession.session_number}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 1, fontSize: 14 }}>
                  <Box sx={{ color: palette.slate, minWidth: 90 }}>Date</Box>
                  <Box sx={{ color: palette.ink, fontWeight: 600 }}>
                    {selectedSession.session_date}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 1, fontSize: 14 }}>
                  <Box sx={{ color: palette.slate, minWidth: 90 }}>Time</Box>
                  <Box sx={{ color: palette.ink, fontWeight: 600 }}>
                    {selectedSession.start_time} - {selectedSession.end_time}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 14 }}>
                  <Box sx={{ color: palette.slate, minWidth: 90 }}>Status</Box>
                  <Chip
                    size="small"
                    label={selectedSession.status}
                    sx={{
                      fontSize: "11.5px",
                      fontWeight: 700,
                      color: "#fff",
                      backgroundColor:
                        (STATUS_COLORS[selectedSession.status] || DEFAULT_STATUS_COLOR).bg,
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 1, fontSize: 14 }}>
                  <Box sx={{ color: palette.slate, minWidth: 90 }}>Type</Box>
                  <Box sx={{ color: palette.ink, fontWeight: 600 }}>
                    {selectedSession.session_type}
                  </Box>
                </Box>

                {selectedSession.notes && (
                  <Box sx={{ display: "flex", gap: 1, fontSize: 14 }}>
                    <Box sx={{ color: palette.slate, minWidth: 90 }}>Notes</Box>
                    <Box sx={{ color: palette.ink }}>{selectedSession.notes}</Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}